import { useMemo } from "react";
import { Card, Table, Tooltip } from "antd";
import formatMoney from "../../../utils/formatMoney";

// Bảng số liệu growth theo ngày. Chỉ hiển thị — fetch và snapshot do container lo.
//
// Cột "Chưa gắn" (orders_unassigned) cố ý để CUỐI và ngoài phễu app/web: phần lớn là
// mảng CTV rải link, Orders.userId ở đó là sub_id của CTV chứ không phải id user LH.
// Giữ cột để thấy nó biến động, KHÔNG cộng vào tỷ trọng app.

const money = (v) => formatMoney.format(Number(v) || 0);
const num = (v) => (Number(v) || 0).toLocaleString("vi-VN");
// Nhóm "sức khoẻ tệp" chỉ chụp được tại thời điểm cron chạy → ngày backfill là 0.
// Hiện "—" để không bị đọc thành "bằng không".
const snap = (v) => (Number(v) > 0 ? num(v) : "—");

function AppGrowthTable({ rows, loading }) {
    const columns = useMemo(
        () => [
            { title: "Ngày", dataIndex: "stat_date", key: "stat_date", fixed: "left", width: 110 },
            {
                title: (
                    <Tooltip title="Người có ≥1 đơn qua app trong 7 ngày tính tới hết ngày này (cửa sổ trượt)">
                        <span>NSM 7d</span>
                    </Tooltip>
                ),
                dataIndex: "nsm_7d",
                key: "nsm_7d",
                align: "right",
                render: num,
            },
            {
                title: (
                    <Tooltip title="Người DISTINCT dán link sản phẩm vào app trong ngày (ClickLog) — bậc thang ngay trước đơn">
                        <span>Người gắn link</span>
                    </Tooltip>
                ),
                dataIndex: "link_users_app",
                key: "link_users_app",
                align: "right",
                render: num,
            },
            { title: "Link app", dataIndex: "links_app", key: "links_app", align: "right", render: num },
            { title: "Đơn app", dataIndex: "orders_app", key: "orders_app", align: "right", render: num },
            { title: "Đơn web", dataIndex: "orders_web", key: "orders_web", align: "right", render: num },
            {
                title: "% app",
                key: "pct_app",
                align: "right",
                render: (_, r) => {
                    const app = Number(r.orders_app) || 0;
                    const assigned = app + (Number(r.orders_web) || 0);
                    return assigned ? `${Math.round((app / assigned) * 1000) / 10}%` : "—";
                },
            },
            {
                title: (
                    <Tooltip title="Doanh số của LH = hoa hồng sàn trả. Orders KHÔNG có cột giá trị đơn (GMV) nên không đo được tổng tiền user mua.">
                        <span>Doanh số app</span>
                    </Tooltip>
                ),
                dataIndex: "commission_app",
                key: "commission_app",
                align: "right",
                render: money,
            },
            {
                title: (
                    <Tooltip title="HH sàn − phần trả user − phần trả người giới thiệu. Ngày gần đây còn tăng vì sàn duyệt đơn dần.">
                        <span>Lợi nhuận gộp</span>
                    </Tooltip>
                ),
                key: "gross_profit_app",
                align: "right",
                render: (_, r) =>
                    money(
                        (Number(r.commission_app) || 0)
                        - (Number(r.user_commission_app) || 0)
                        - (Number(r.ref_commission_app) || 0)
                    ),
            },
            { title: "HH web", dataIndex: "commission_web", key: "commission_web", align: "right", render: money },
            { title: "Mua app/ngày", dataIndex: "users_app_day", key: "users_app_day", align: "right", render: num },
            { title: "Cài iOS", dataIndex: "installs_ios", key: "installs_ios", align: "right", render: num },
            { title: "Cài Android", dataIndex: "installs_android", key: "installs_android", align: "right", render: num },
            {
                title: (
                    <Tooltip title="Trong số người cài app ngày đó, bao nhiêu ra đơn app trong 72h. 3 ngày cuối chưa đủ 72h nên còn tăng.">
                        <span>Đơn ≤72h</span>
                    </Tooltip>
                ),
                dataIndex: "installs_ordered_72h",
                key: "installs_ordered_72h",
                align: "right",
                render: num,
            },
            { title: "User mới", dataIndex: "new_users", key: "new_users", align: "right", render: num },
            { title: "Có ref", dataIndex: "new_users_ref", key: "new_users_ref", align: "right", render: num },
            {
                title: (
                    <Tooltip title="Chụp tại thời điểm cron chạy, không tái tạo được cho quá khứ — ngày backfill hiện '—'">
                        <span>Tệp có app</span>
                    </Tooltip>
                ),
                dataIndex: "app_users_total",
                key: "app_users_total",
                align: "right",
                render: snap,
            },
            {
                title: (
                    <Tooltip title="Người mở app trong 30 ngày (lastSeen). Ảnh chụp — không có lịch sử theo ngày.">
                        <span>Mở app 30d</span>
                    </Tooltip>
                ),
                dataIndex: "app_users_open_30d",
                key: "app_users_open_30d",
                align: "right",
                render: snap,
            },
            { title: "HĐ 30d", dataIndex: "app_users_active_30d", key: "app_users_active_30d", align: "right", render: snap },
            { title: "Ngủ đông", dataIndex: "app_users_dormant", key: "app_users_dormant", align: "right", render: snap },
            { title: "Không push được", dataIndex: "push_gap_buyers", key: "push_gap_buyers", align: "right", render: snap },
            { title: "Chỉ web 30d", dataIndex: "web_only_users_30d", key: "web_only_users_30d", align: "right", render: snap },
            {
                title: (
                    <Tooltip title="Đơn channel NULL — phần lớn là mảng CTV rải link, KHÔNG thuộc phễu app/web">
                        <span>Chưa gắn</span>
                    </Tooltip>
                ),
                dataIndex: "orders_unassigned",
                key: "orders_unassigned",
                align: "right",
                render: num,
            },
        ],
        []
    );

    return (
        <Card title="Số liệu growth theo ngày" className="shadow-sm">
            <Table
                rowKey="stat_date"
                columns={columns}
                dataSource={rows}
                loading={loading}
                size="small"
                scroll={{ x: "max-content" }}
                pagination={{ pageSize: 15, showSizeChanger: false }}
            />
        </Card>
    );
}

export default AppGrowthTable;
